/**
 * กรอกแบบประเมิน (/me/evaluations/:id) — เรนเดอร์ตาม template (ตนเอง/หัวหน้า/เพื่อน)
 * ให้คะแนน 1–5 ต่อหัวข้อ + คำถามปลายเปิด (+ สรุปผล ถ้าเป็นฟอร์มหัวหน้า) → ส่งเข้าระบบ
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { useEvaluation, useSubmitEvaluation } from '../hooks';
import { useToast } from '../components/ui/toast';
import { EVAL_SCALE, EVAL_OUTCOMES, type EvalTemplate } from '@payroll/shared';

interface EvalDetail {
  id: string;
  role: 'self' | 'supervisor' | 'peer';
  status: string;
  answers: { scores?: Record<string, number>; texts?: Record<string, string> } | null;
  outcome: string | null;
  template: EvalTemplate;
  subject_name: string;
  subject_dept: string;
  title: string;
  due_date: string;
  locked: boolean;
}

export function EvaluationForm() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const toast = useToast();
  const { data, isLoading } = useEvaluation(id);
  const submit = useSubmitEvaluation();

  const d = data as unknown as EvalDetail | undefined;
  const [scores, setScores] = useState<Record<string, number>>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<string>('');

  useEffect(() => {
    if (d?.answers) {
      setScores(d.answers.scores ?? {});
      setTexts(d.answers.texts ?? {});
    }
    if (d?.outcome) setOutcome(d.outcome);
  }, [d]);

  if (isLoading || !d) return <PageLayout title="แบบประเมิน"><p className="py-8 text-center text-gray-400">กำลังโหลด…</p></PageLayout>;

  const tmpl = d.template;
  const allScored = tmpl.items.every((it) => { const v = scores[it.key] ?? 0; return v >= 1 && v <= 5; });
  const totalNow = tmpl.items.reduce((s, it) => s + (scores[it.key] || 0), 0);

  async function onSubmit() {
    if (!allScored) { toast.error('กรุณาให้คะแนนครบทุกหัวข้อ'); return; }
    if (tmpl.hasOutcome && !outcome) { toast.error('กรุณาเลือกสรุปผลการประเมิน'); return; }
    try {
      await submit.mutateAsync({ id: d!.id, scores, texts, outcome: tmpl.hasOutcome ? outcome : null });
      toast.success('ส่งแบบประเมินเรียบร้อย');
      nav('/me/evaluations');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ส่งไม่สำเร็จ');
    }
  }

  return (
    <PageLayout title={tmpl.title}>
      <div className="mb-4 text-sm text-gray-600">
        {d.role !== 'self' && <div>ผู้ถูกประเมิน: <b>{d.subject_name}</b> {d.subject_dept && `· ${d.subject_dept}`}</div>}
        <div className="text-gray-400">{d.title} · ครบกำหนด {d.due_date}</div>
      </div>

      {d.locked && (
        <div className="mb-4 p-3 rounded-lg bg-gray-100 text-gray-500 text-sm">รอบนี้ปิดแล้ว — ดูได้อย่างเดียว</div>
      )}

      <div className="space-y-4">
        {tmpl.items.map((it, idx) => (
          <div key={it.key} className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="text-sm font-medium text-gray-800 mb-2">{idx + 1}. {it.label}</div>
            <div className="flex gap-1.5 flex-wrap">
              {EVAL_SCALE.map((s) => (
                <button
                  key={s.value}
                  disabled={d.locked}
                  onClick={() => setScores((p) => ({ ...p, [it.key]: s.value }))}
                  className={`flex-1 min-w-[56px] py-1.5 rounded-lg border text-xs transition ${
                    scores[it.key] === s.value
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-gray-500 border-gray-200'
                  }`}
                >
                  <div className="font-bold">{s.value}</div>
                  <div className="text-[10px] opacity-80">{s.label}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 text-right text-sm text-gray-500">รวม {totalNow} / {tmpl.maxScore} คะแนน</div>

      <div className="mt-4 space-y-3">
        {tmpl.openQuestions.map((q) => (
          <div key={q.key}>
            <label className="block text-sm text-gray-700 mb-1">{q.label}</label>
            <textarea
              disabled={d.locked}
              value={texts[q.key] ?? ''}
              onChange={(e) => setTexts((p) => ({ ...p, [q.key]: e.target.value }))}
              rows={2}
              className="w-full p-2 rounded-lg border border-gray-200 text-sm"
            />
          </div>
        ))}
      </div>

      {tmpl.hasOutcome && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">สรุปผลการประเมิน</label>
          <div className="space-y-1.5">
            {EVAL_OUTCOMES.map((o) => (
              <label key={o.key} className="flex items-center gap-2 text-sm">
                <input type="radio" name="outcome" disabled={d.locked} checked={outcome === o.key} onChange={() => setOutcome(o.key)} />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {!d.locked && (
        <button
          onClick={onSubmit}
          disabled={submit.isPending}
          className="mt-6 w-full py-2.5 rounded-xl bg-slate-900 text-white font-medium disabled:opacity-50"
        >
          {submit.isPending ? 'กำลังส่ง…' : d.status === 'submitted' ? 'บันทึกการแก้ไข' : 'ส่งแบบประเมิน'}
        </button>
      )}
    </PageLayout>
  );
}
